// ONU bind — a write action on the subscriber detail page's ONU section.
// Shows a "Bind to <username>" button *only when the ONU's current
// description doesn't already match this subscriber* (a desc mismatch you
// can fix); it sets the ONU's description on its OLT to the username via
// /v1/api/cmd/olt_assign. Gated by `olt:write`. There's deliberately no
// "Unbind" here — clearing a desc lives on the OLT management pages, not
// on a subscriber's lookup view; this just fixes the common case where an
// ONU is mapped to the wrong / no name.
//
// The Go `olt_assign` handler needs `onu_name` ("<TYPE>0<port>ONU<index>",
// TYPE = GPON|EPON by the OLT's driver model), which the per-username ONU
// record doesn't carry — so we reconstruct it from the OLT's `model`
// (looked up via useOlts) + the record's pon/onu. If the model can't be
// resolved, the action doesn't render (better than guessing wrong).

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from "@noc/ui";
import { ApiError } from "@noc/api";
import { api } from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { useCan, useCanOLT } from "@/features/auth/roles";
import { useOlts } from "@/features/fleet/useFleet";

/** Build the canonical ONU name the OLT CLI uses. Mirrors the pollers'
 *  `<TYPE>0%dONU%d` (slot 0, then PON port, then ONU index). */
function buildOnuName(model: string | undefined, pon: number, onu: number): string | null {
  if (!model) return null;
  const m = model.toLowerCase();
  const type = m.includes("gpon") ? "GPON" : m.includes("epon") ? "EPON" : null;
  if (!type) return null;
  return `${type}0${pon}ONU${onu}`;
}

export function OnuBindActions({
  subscriber,
  onu,
}: {
  subscriber: string;
  onu: { olt?: unknown; pon_port?: unknown; onu_index?: unknown; username?: unknown };
}) {
  const oltName = typeof onu.olt === "string" ? onu.olt : "";
  // Needs the global "may write to OLTs" permission *and*, for a
  // reseller account, this particular OLT in its grants.
  const canWriteOLT = useCan("olt:write");
  const oltGranted = useCanOLT(oltName || null);
  const allowed = canWriteOLT && oltGranted;
  const qc = useQueryClient();
  const oltsQ = useOlts();
  const [open, setOpen] = useState(false);

  const pon = typeof onu.pon_port === "number" ? onu.pon_port : null;
  const onuIdx = typeof onu.onu_index === "number" ? onu.onu_index : null;
  const currentDesc = typeof onu.username === "string" ? onu.username : "";

  const model = oltsQ.data?.rows?.find((r) => r.name === oltName)?.model;
  const onuName = pon != null && onuIdx != null ? buildOnuName(model, pon, onuIdx) : null;

  const mutation = useMutation({
    mutationFn: () => api.cmd.oltAssign({ olt_name: oltName, onu_name: onuName!, username: subscriber }),
    onSuccess: (res) => {
      if (!res.ok) {
        toast.error("Bind failed", { description: res.error });
        return;
      }
      toast.success(`ONU bound to ${subscriber}`, {
        description: "Applied on the OLT — the ONU section here refreshes on the next OLT poll.",
      });
      setOpen(false);
      void qc.invalidateQueries({ queryKey: qk.subscriber.all(subscriber) });
    },
    onError: (e) => {
      toast.error("Bind failed", {
        description: e instanceof ApiError ? e.message : e instanceof Error ? e.message : "request failed",
      });
    },
  });

  // Render nothing unless: the user can write to OLTs, we resolved the
  // ONU's CLI name, and the ONU's current description doesn't already
  // match this subscriber (i.e. there's a mismatch worth fixing).
  if (!allowed || !oltName || !onuName || currentDesc === subscriber) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && setOpen(o)}>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Link2 /> Bind to {subscriber}
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Bind {oltName} / {onuName} to {subscriber}?
          </DialogTitle>
          <DialogDescription>
            Sets this ONU&apos;s description on the OLT to <code>{subscriber}</code>
            {currentDesc ? (
              <>
                {" "}
                (currently <code>{currentDesc}</code>)
              </>
            ) : null}
            . This is how the portal maps a PPPoE username to a registered ONU.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Binding…" : "Bind"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
